import { useEffect, useState, useCallback, useRef } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Bank, ArrowClockwise, Warning, LinkSimple, XCircle, ChartLine } from "@phosphor-icons/react";
import { toast } from "sonner";
import { buildNextEvt, eventWithin } from "@/lib/fxEvents";

const fmt = (n, d = 2) => (n === null || n === undefined ? "—" : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d }));
const pairLabel = (p) => `${p.slice(0, 3)}/${p.slice(3)}`;

function LinkCard({ onLinked }) {
  const [env, setEnv] = useState("practice");
  const [accountId, setAccountId] = useState("");
  const [token, setToken] = useState("");
  const [confirmLive, setConfirmLive] = useState(false);
  const [busy, setBusy] = useState(false);

  const link = async () => {
    if (!accountId.trim() || !token.trim()) return toast.error("Enter your account ID and API token");
    if (env === "live" && !confirmLive) return toast.error("Tick the LIVE confirmation to link a real-money account");
    setBusy(true);
    try {
      const r = await api.post("/oanda/link", { token: token.trim(), account_id: accountId.trim(), environment: env, confirm_live: confirmLive });
      toast.success(`OANDA ${env} account linked`, { description: `${r.data.account_id} · ${r.data.currency || ""}` });
      setToken("");
      window.dispatchEvent(new Event("oanda-status-changed"));
      onLinked();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-5 space-y-4" data-testid="oanda-link-card">
      <div className="flex gap-2">
        {["practice", "live"].map((v) => (
          <button key={v} data-testid={`oanda-env-${v}`} onClick={() => setEnv(v)}
            className={`mono text-[11px] uppercase px-3 py-1.5 rounded-sm border transition-colors duration-150 ${env === v ? (v === "live" ? "border-signal-red bg-signal-red/15 text-signal-red" : "border-volt bg-volt/15 text-volt") : "border-border text-muted-foreground hover:text-white"}`}>{v}{v === "practice" ? " · demo" : " · real money"}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <div className="label-xs text-muted-foreground mb-1">Account ID</div>
          <Input data-testid="oanda-account-input" value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="001-001-1234567-001" className="bg-background rounded-sm mono h-10" />
        </div>
        <div>
          <div className="label-xs text-muted-foreground mb-1">v20 API Token</div>
          <Input data-testid="oanda-token-input" type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder="paste your token" className="bg-background rounded-sm mono h-10" />
        </div>
      </div>
      {env === "live" && (
        <label className="flex items-start gap-2 text-xs text-signal-red cursor-pointer" data-testid="oanda-live-confirm-label">
          <Checkbox data-testid="oanda-live-confirm" checked={confirmLive} onCheckedChange={setConfirmLive} className="mt-0.5 border-signal-red data-[state=checked]:bg-signal-red" />
          <span>I understand this links a <b>LIVE real-money</b> OANDA account and orders will use real funds.</span>
        </label>
      )}
      <Button data-testid="oanda-link-btn" onClick={link} disabled={busy} className="rounded-sm h-10 bg-volt hover:bg-volt/90 text-white gap-2"><LinkSimple size={16} /> {busy ? "Validating…" : "Validate & Link"}</Button>
      <p className="mono text-[10px] text-muted-foreground">Get your token in OANDA → My Account → My Services → Manage API Access. Practice & Live tokens are separate. Stored encrypted, never shown again.</p>
    </div>
  );
}

export default function OandaBroker() {
  const [status, setStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [err, setErr] = useState(null);
  const [ticket, setTicket] = useState({ pair: "EURUSD", side: "buy", units: "1000", sl: "", tp: "", trail: "" });
  const [confirmLive, setConfirmLive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dash, setDash] = useState(null);
  const [nextEvt, setNextEvt] = useState({});
  const timer = useRef(null);

  const isLive = status?.active_env === "live";

  useEffect(() => { api.get("/forex/economic?impact=high").then((r) => setNextEvt(buildNextEvt(r.data.events))).catch(() => {}); }, []);

  const loadStatus = useCallback(async () => {
    try { const r = await api.get("/oanda/status"); setStatus(r.data); return r.data; }
    catch (e) { setStatus({ linked: false }); return { linked: false }; }
  }, []);

  const loadData = useCallback(async () => {
    setErr(null);
    try {
      const [s, p, d] = await Promise.all([api.get("/oanda/summary"), api.get("/oanda/positions"), api.get("/oanda/dashboard")]);
      setSummary(s.data); setPositions(p.data.positions || []); setDash(d.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load broker data"); }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);
  useEffect(() => {
    if (status?.linked) {
      loadData();
      timer.current = setInterval(loadData, 15000);
      return () => clearInterval(timer.current);
    }
  }, [status?.linked, status?.active_env, loadData]);

  const switchEnv = async (env) => {
    if (env === "live" && status?.active_env !== "live" &&
        !window.confirm("Switch to your LIVE OANDA account?\n\nOrders you place will use REAL money. You'll still confirm each individual live order.")) return;
    try { await api.post("/oanda/switch", { env }); toast.success(`Switched to ${env}`); window.dispatchEvent(new Event("oanda-status-changed")); const s = await loadStatus(); if (s.linked) loadData(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const unlink = async () => {
    if (!window.confirm(`Unlink your OANDA ${status.active_env} account? (Your OANDA account is untouched.)`)) return;
    try { await api.post("/oanda/unlink", { env: status.active_env }); toast.success("Unlinked"); setSummary(null); setPositions([]); window.dispatchEvent(new Event("oanda-status-changed")); loadStatus(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  // Break-even auto-stop
  const [be, setBe] = useState(null);
  const [beBusy, setBeBusy] = useState(false);
  const [unprotectedOnly, setUnprotectedOnly] = useState(false);
  const [recap, setRecap] = useState(null);
  const [weekly, setWeekly] = useState(null);
  useEffect(() => {
    if (status?.linked) {
      api.get("/oanda/breakeven").then((r) => setBe(r.data)).catch(() => {});
      api.get("/oanda/recap").then((r) => setRecap(r.data)).catch(() => {});
      api.get("/oanda/weekly").then((r) => setWeekly(r.data)).catch(() => {});
    }
  }, [status?.linked]);
  const toggleRecap = async (v) => {
    setRecap({ enabled: v });
    try { const r = await api.post("/oanda/recap", { enabled: v }); setRecap(r.data); toast.success(v ? "Daily Telegram recap on" : "Daily recap off"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendTestRecap = async () => {
    try { await api.post("/oanda/recap/send"); toast.success("Recap sent to your Telegram"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const toggleWeekly = async (v) => {
    setWeekly({ enabled: v });
    try { const r = await api.post("/oanda/weekly", { enabled: v }); setWeekly(r.data); toast.success(v ? "Weekly digest on (Saturdays)" : "Weekly digest off"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const sendTestWeekly = async () => {
    try { await api.post("/oanda/weekly/send"); toast.success("Weekly digest sent to your Telegram"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveBe = async (patch) => {
    const next = { ...be, ...patch };
    setBe(next); setBeBusy(true);
    try {
      const r = await api.post("/oanda/breakeven", { enabled: next.enabled, trigger_pips: Number(next.trigger_pips), buffer_pips: Number(next.buffer_pips), trail_after_pips: Number(next.trail_after_pips) || 0 });
      setBe(r.data);
      if (patch.enabled !== undefined) toast.success(patch.enabled ? "Break-even auto-stop on" : "Break-even auto-stop off");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBeBusy(false); }
  };
  const applyBe = async () => {
    setBeBusy(true);
    try {
      const r = await api.post("/oanda/breakeven/apply");
      const n = (r.data.moved || []).length;
      toast.success(n ? `Moved ${n} stop(s) to break-even` : `No trades qualified yet (checked ${r.data.checked || 0})`);
      loadData();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBeBusy(false); }
  };

  const submit = async () => {
    const units = parseFloat(ticket.units);
    if (!units || units <= 0) return toast.error("Enter a valid unit size");
    if (isLive && !confirmLive) return toast.error("Tick the LIVE confirmation to place a real-money order");
    const guard = eventWithin(nextEvt, ticket.pair, 60);
    if (guard && !window.confirm(`⚠ High-impact ${guard.currency} event ("${guard.title}") is due within the hour. Volatility can spike — place this ${pairLabel(ticket.pair)} order anyway?`)) return;
    setBusy(true);
    try {
      const body = { pair: ticket.pair, side: ticket.side, units, confirm_live: confirmLive };
      if (Number(ticket.sl) > 0) body.stop_pips = Number(ticket.sl);
      if (Number(ticket.tp) > 0) body.tp_pips = Number(ticket.tp);
      if (Number(ticket.trail) > 0) body.trail_pips = Number(ticket.trail);
      const r = await api.post("/oanda/orders", body);
      setSummary(r.data.summary);
      const bracket = [r.data.trail_pips ? `Trail ${r.data.trail_pips}p` : (r.data.stop_pips ? `SL ${r.data.stop_pips}p` : null), r.data.tp_pips ? `TP ${r.data.tp_pips}p` : null].filter(Boolean).join(" · ");
      toast.success(`${ticket.side === "buy" ? "Bought" : "Sold"} ${units.toLocaleString()} ${pairLabel(ticket.pair)} @ ${r.data.price}`, { description: bracket || undefined });
      loadData();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const closePos = async (pair) => {
    if (isLive && !window.confirm(`Close your LIVE ${pairLabel(pair)} position at market?`)) return;
    try { const r = await api.post(`/oanda/positions/${pair}/close`); toast.success(`Closed ${pairLabel(pair)}`); setSummary(r.data.summary); loadData(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setLevels = async (pair) => {
    const s = window.prompt(`Stop-loss for ${pairLabel(pair)} in pips from entry (blank = leave unchanged):`, "30");
    if (s === null) return;
    const t = window.prompt(`Take-profit for ${pairLabel(pair)} in pips from entry (blank = leave unchanged):`, "60");
    if (t === null) return;
    const body = {};
    if (s.trim()) body.stop_pips = parseFloat(s);
    if (t.trim()) body.tp_pips = parseFloat(t);
    if (!body.stop_pips && !body.tp_pips) return toast.error("Enter a stop and/or target in pips");
    try { await api.post(`/oanda/positions/${pair}/levels`, body); toast.success(`Stop/target set on ${pairLabel(pair)}`); loadData(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stopAll = async () => {
    if (!window.confirm(`STOP ALL: close every ${isLive ? "LIVE " : ""}position at market and switch FX signals off?`)) return;
    try {
      const r = await api.post("/oanda/positions/close-all");
      const n = r.data.closed.length, f = r.data.failed.length;
      (f ? toast.error : toast.success)(`Closed ${n} position(s)${f ? `, ${f} failed` : ""}. FX signals off.`);
      setSummary(r.data.summary); loadData();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="oanda-panel">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="label-xs flex items-center gap-2"><Bank size={15} weight="fill" className="text-volt" /> Live FX Broker · OANDA <span className="mono text-[9px] uppercase px-1.5 py-0.5 rounded-sm bg-volt/15 text-volt">Elite</span></span>
        {status?.linked && (
          <div className="flex items-center gap-3">
            {status.envs_linked && (status.envs_linked.practice && status.envs_linked.live) ? (
              <div className="flex items-center gap-2" data-testid="oanda-env-switch">
                <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground hidden sm:inline">Mode</span>
                <div className="flex rounded-md overflow-hidden border border-border">
                  {["practice", "live"].map((v) => {
                    const on = status.active_env === v;
                    return (
                      <button key={v} data-testid={`oanda-switch-${v}`} onClick={() => switchEnv(v)}
                        className={`mono text-[11px] font-semibold uppercase px-3 py-1.5 transition-colors duration-150 ${
                          on
                            ? (v === "live" ? "bg-signal-red text-white shadow-[0_0_12px_rgba(239,68,68,0.5)]" : "bg-volt text-white")
                            : (v === "live" ? "text-signal-red hover:bg-signal-red/10" : "text-muted-foreground hover:text-white hover:bg-white/5")
                        }`}>
                        {v === "practice" ? "Practice" : "Live"}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <span className={`mono text-[10px] uppercase px-2 py-0.5 rounded-sm ${isLive ? "bg-signal-red/15 text-signal-red" : "bg-volt/15 text-volt"}`} data-testid="oanda-env-badge">{isLive ? "LIVE · REAL MONEY" : "PRACTICE · DEMO"}</span>
            )}
            <button data-testid="oanda-unlink-btn" onClick={unlink} className="text-xs text-muted-foreground hover:text-signal-red">Unlink</button>
          </div>
        )}
      </div>

      {!status ? (
        <div className="px-4 py-8 text-center text-muted-foreground mono text-xs">Loading…</div>
      ) : !status.linked ? (
        <LinkCard onLinked={loadStatus} />
      ) : (
        <div className="p-5 space-y-5">
          {isLive && (
            <div className="flex items-center gap-2 border border-signal-red/40 bg-signal-red/5 text-signal-red rounded-sm px-3 py-2 mono text-[11px]" data-testid="oanda-live-warning">
              <Warning size={14} weight="fill" /> Live account — every order below uses REAL money.
            </div>
          )}
          {err ? (
            <div className="flex items-center justify-between border border-signal-red/40 bg-signal-red/5 rounded-sm px-3 py-2">
              <span className="mono text-[11px] text-signal-red" data-testid="oanda-error">{err}</span>
              <Button size="sm" variant="outline" onClick={loadData} className="rounded-sm border-border h-7 gap-1"><ArrowClockwise size={13} /> Retry</Button>
            </div>
          ) : summary && (
            <div className="flex flex-wrap gap-5" data-testid="oanda-summary">
              <Stat label={`NAV (${summary.currency || ""})`} value={fmt(summary.nav)} />
              <Stat label="Balance" value={fmt(summary.balance)} />
              <Stat label="Unrealized P/L" value={`${summary.unrealized_pl >= 0 ? "+" : "-"}${fmt(Math.abs(summary.unrealized_pl))}`} accent={summary.unrealized_pl >= 0 ? "text-signal-green" : "text-signal-red"} />
              <Stat label="Margin Used" value={fmt(summary.margin_used)} />
              <Stat label="Margin Avail." value={fmt(summary.margin_available)} />
              <Stat label="Open Positions" value={summary.open_position_count} />
            </div>
          )}

          {/* Order ticket */}
          <div className="border border-border rounded-sm p-4">
            <div className="label-xs mb-3">Place Broker Order (Market)</div>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <div className="label-xs text-muted-foreground mb-1">Pair</div>
                <Input data-testid="oanda-order-pair" value={ticket.pair} onChange={(e) => setTicket({ ...ticket, pair: e.target.value.toUpperCase().replace("/", "") })} className="bg-background rounded-sm mono h-10 w-[120px]" />
              </div>
              <div>
                <div className="label-xs text-muted-foreground mb-1">Side</div>
                <div className="flex rounded-sm overflow-hidden border border-border h-10">
                  <button data-testid="oanda-side-buy" onClick={() => setTicket({ ...ticket, side: "buy" })} className={`px-4 mono text-sm ${ticket.side === "buy" ? "bg-signal-green/20 text-signal-green" : "text-muted-foreground"}`}>BUY</button>
                  <button data-testid="oanda-side-sell" onClick={() => setTicket({ ...ticket, side: "sell" })} className={`px-4 mono text-sm ${ticket.side === "sell" ? "bg-signal-red/20 text-signal-red" : "text-muted-foreground"}`}>SELL</button>
                </div>
              </div>
              <div>
                <div className="label-xs text-muted-foreground mb-1">Units</div>
                <Input data-testid="oanda-units" type="number" value={ticket.units} onChange={(e) => setTicket({ ...ticket, units: e.target.value })} className="bg-background rounded-sm mono h-10 w-[120px]" />
              </div>
              <div>
                <div className="label-xs text-muted-foreground mb-1">Stop (pips)</div>
                <Input data-testid="oanda-sl-pips" type="number" step="any" value={ticket.sl} onChange={(e) => setTicket({ ...ticket, sl: e.target.value })} placeholder="opt." className="bg-background rounded-sm mono h-10 w-[90px]" />
              </div>
              <div>
                <div className="label-xs text-muted-foreground mb-1">Target (pips)</div>
                <Input data-testid="oanda-tp-pips" type="number" step="any" value={ticket.tp} onChange={(e) => setTicket({ ...ticket, tp: e.target.value })} placeholder="opt." className="bg-background rounded-sm mono h-10 w-[90px]" />
              </div>
              <div>
                <div className="label-xs text-muted-foreground mb-1">Trail (pips)</div>
                <Input data-testid="oanda-trail-pips" type="number" step="any" value={ticket.trail} onChange={(e) => setTicket({ ...ticket, trail: e.target.value })} placeholder="opt." className="bg-background rounded-sm mono h-10 w-[90px]" />
              </div>
              <Button data-testid="oanda-submit-order" onClick={submit} disabled={busy} className={`rounded-sm h-10 ${ticket.side === "buy" ? "bg-signal-green hover:bg-signal-green/90 !text-black" : "bg-signal-red hover:bg-signal-red/90 text-white"}`}>{busy ? "Placing…" : `${ticket.side === "buy" ? "Buy" : "Sell"} ${pairLabel(ticket.pair)}`}</Button>
            </div>
            <p className="mono text-[10px] text-muted-foreground mt-2">Optional bracket in pips — Stop &amp; Target attach on fill. A Trail (pips) sets a trailing stop instead of a fixed one, so winners can run.</p>
            {(() => { const g = eventWithin(nextEvt, ticket.pair, 60); return g ? (
              <div className="flex items-start gap-2 border border-signal-red/40 bg-signal-red/5 text-signal-red rounded-sm px-3 py-2 mono text-[11px] mt-2" data-testid="oanda-risk-guard">
                <Warning size={13} weight="fill" className="mt-0.5 shrink-0" /> High-impact {g.currency} event ("{g.title}") within the hour — you'll be asked to confirm before this order goes through.
              </div>
            ) : null; })()}
            {isLive && (
              <label className="flex items-center gap-2 text-xs text-signal-red cursor-pointer mt-3">
                <Checkbox data-testid="oanda-order-live-confirm" checked={confirmLive} onCheckedChange={setConfirmLive} className="border-signal-red data-[state=checked]:bg-signal-red" />
                <span>Confirm this LIVE real-money order</span>
              </label>
            )}
          </div>

          {/* Break-even auto-stop */}
          {be && (
            <div className="border border-border rounded-sm p-4" data-testid="oanda-breakeven">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox data-testid="oanda-be-enabled" checked={be.enabled} onCheckedChange={(v) => saveBe({ enabled: !!v })} className="border-volt data-[state=checked]:bg-volt" />
                  <span className="label-xs">Break-even auto-stop</span>
                </label>
                <div className="flex items-end gap-3">
                  <div>
                    <div className="label-xs text-muted-foreground mb-1">Trigger (pips)</div>
                    <Input data-testid="oanda-be-trigger" type="number" step="any" value={be.trigger_pips} onChange={(e) => setBe({ ...be, trigger_pips: e.target.value })} onBlur={() => saveBe({})} className="bg-background rounded-sm mono h-9 w-[90px]" />
                  </div>
                  <div>
                    <div className="label-xs text-muted-foreground mb-1">Buffer (pips)</div>
                    <Input data-testid="oanda-be-buffer" type="number" step="any" value={be.buffer_pips} onChange={(e) => setBe({ ...be, buffer_pips: e.target.value })} onBlur={() => saveBe({})} className="bg-background rounded-sm mono h-9 w-[90px]" />
                  </div>
                  <div>
                    <div className="label-xs text-muted-foreground mb-1">Then trail (pips)</div>
                    <Input data-testid="oanda-be-trail" type="number" step="any" placeholder="off" value={be.trail_after_pips || ""} onChange={(e) => setBe({ ...be, trail_after_pips: e.target.value })} onBlur={() => saveBe({})} className="bg-background rounded-sm mono h-9 w-[90px]" />
                  </div>
                  <Button data-testid="oanda-be-apply" onClick={applyBe} disabled={beBusy} variant="outline" className="rounded-sm h-9 border-volt/40 text-volt hover:bg-volt/10">Apply now</Button>
                </div>
              </div>
              <p className="mono text-[10px] text-muted-foreground mt-2">When a trade is up {be.trigger_pips || 0} pips, {Number(be.trail_after_pips) > 0 ? `its stop switches to a ${be.trail_after_pips}-pip trailing stop so profits keep locking in` : `its stop auto-moves to entry ${Number(be.buffer_pips) > 0 ? `+${be.buffer_pips} pip(s)` : ""} — locking in a free ride`}. Checked automatically every ~45s while enabled.</p>
              {recap && (
                <div className="flex items-center justify-between gap-3 flex-wrap mt-3 pt-3 border-t border-border/50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox data-testid="oanda-recap-enabled" checked={recap.enabled} onCheckedChange={(v) => toggleRecap(!!v)} className="border-volt data-[state=checked]:bg-volt" />
                    <span className="label-xs">Daily Telegram recap</span>
                    <span className="mono text-[10px] text-muted-foreground">— end-of-day P/L, stops moved &amp; open risk (~21:00 UTC)</span>
                  </label>
                  <Button data-testid="oanda-recap-test" onClick={sendTestRecap} variant="outline" className="rounded-sm h-8 border-border text-muted-foreground hover:text-white">Send test now</Button>
                </div>
              )}
              {weekly && (
                <div className="flex items-center justify-between gap-3 flex-wrap mt-3 pt-3 border-t border-border/50">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <Checkbox data-testid="oanda-weekly-enabled" checked={weekly.enabled} onCheckedChange={(v) => toggleWeekly(!!v)} className="border-volt data-[state=checked]:bg-volt" />
                    <span className="label-xs">Weekly Telegram digest</span>
                    <span className="mono text-[10px] text-muted-foreground">— Saturday summary: week P/L, win rate &amp; best/worst pairs</span>
                  </label>
                  <Button data-testid="oanda-weekly-test" onClick={sendTestWeekly} variant="outline" className="rounded-sm h-8 border-border text-muted-foreground hover:text-white">Send test now</Button>
                </div>
              )}
            </div>
          )}

          {/* Positions */}
          {positions.length > 0 && (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="oanda-positions">
              <div className="border-b border-border px-4 py-2.5 flex items-center justify-between gap-3">
                <span className="label-xs">Open Broker Positions ({positions.length})</span>
                <button data-testid="oanda-stop-all" onClick={stopAll} disabled={positions.length === 0}
                  className="ml-auto text-xs px-3 py-1 border border-signal-red text-signal-red hover:bg-signal-red hover:text-white disabled:opacity-40">
                  Stop All
                </button>
                {positions.some((p) => !p.protected) && (
                  <label className="flex items-center gap-2 cursor-pointer mono text-[10px] text-muted-foreground">
                    <Checkbox data-testid="oanda-unprotected-filter" checked={unprotectedOnly} onCheckedChange={(v) => setUnprotectedOnly(!!v)} className="h-3.5 w-3.5 border-signal-red data-[state=checked]:bg-signal-red" />
                    Unprotected only ({positions.filter((p) => !p.protected).length})
                  </label>
                )}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="label-xs text-muted-foreground border-b border-border/60">
                    {["Pair", "Side", "Units", "Avg", "Stop", "Target", "P/L", ""].map((h) => <th key={h} className="text-left font-normal px-4 py-2">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {positions.filter((p) => !unprotectedOnly || !p.protected).map((p) => (
                      <tr key={p.pair} className="border-b border-border/40" data-testid={`oanda-pos-${p.pair}`}>
                        <td className="px-4 py-2.5 mono font-medium">
                          <div className="flex items-center gap-1.5">
                            {pairLabel(p.pair)}
                            {p.protection && (
                              <span data-testid={`oanda-protection-${p.pair}`} title={p.protection === "trailing" ? "Trailing stop active" : "Stop moved to break-even — risk-free"}
                                className="mono text-[8px] uppercase px-1 py-0.5 rounded-sm bg-signal-green/15 text-signal-green border border-signal-green/30">
                                {p.protection === "trailing" ? "TRAIL" : "BE"}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className={`px-4 py-2.5 mono uppercase text-xs ${p.side === "long" ? "text-signal-green" : "text-signal-red"}`}>{p.side}</td>
                        <td className="px-4 py-2.5 mono">{fmt(p.units, 0)}</td>
                        <td className="px-4 py-2.5 mono">{p.avg_price != null ? fmt(p.avg_price, 5) : "—"}</td>
                        <td className={`px-4 py-2.5 mono ${p.stop_price != null ? "text-signal-red" : "text-muted-foreground"}`} data-testid={`oanda-stop-${p.pair}`}>{p.stop_price != null ? fmt(p.stop_price, 5) : "none"}</td>
                        <td className={`px-4 py-2.5 mono ${p.target_price != null ? "text-signal-green" : "text-muted-foreground"}`} data-testid={`oanda-target-${p.pair}`}>{p.target_price != null ? fmt(p.target_price, 5) : "none"}</td>
                        <td className={`px-4 py-2.5 mono ${p.unrealized_pl >= 0 ? "text-signal-green" : "text-signal-red"}`}>{p.unrealized_pl >= 0 ? "+" : "-"}{fmt(Math.abs(p.unrealized_pl))}</td>
                        <td className="px-4 py-2.5 whitespace-nowrap"><button data-testid={`oanda-levels-${p.pair}`} onClick={() => setLevels(p.pair)} className="text-xs text-signal-cyan hover:underline mr-3">Set stop/target</button><button data-testid={`oanda-close-${p.pair}`} onClick={() => closePos(p.pair)} className="text-xs text-muted-foreground hover:text-signal-red">Close</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Analytics: equity curve + positions × AI signal */}
          {dash && (dash.equity_history?.length > 1 || dash.positions?.length > 0) && (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="oanda-analytics">
              <div className="border-b border-border px-4 py-2.5 label-xs flex items-center gap-2"><ChartLine size={14} className="text-volt" /> Broker Analytics</div>
              {dash.equity_history?.length > 1 && (
                <div className="p-4 border-b border-border/50">
                  <div className="label-xs text-muted-foreground mb-2">NAV since you connected ({summary?.currency})</div>
                  <div className="h-[180px] w-full">
                    <ResponsiveContainer width="100%" height="100%" minWidth={0}>
                      <AreaChart data={dash.equity_history} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                        <defs><linearGradient id="oandaNav" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C6FF00" stopOpacity={0.35} /><stop offset="100%" stopColor="#C6FF00" stopOpacity={0} /></linearGradient></defs>
                        <XAxis dataKey="ts" tick={{ fontSize: 9, fill: "#8A8A93" }} tickFormatter={(t) => { try { return new Date(t).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } }} minTickGap={40} />
                        <YAxis tick={{ fontSize: 9, fill: "#8A8A93" }} width={60} domain={["auto", "auto"]} />
                        <Tooltip contentStyle={{ background: "#141415", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 4, fontSize: 12 }} />
                        <Area type="monotone" dataKey="nav" stroke="#C6FF00" strokeWidth={1.6} fill="url(#oandaNav)" dot={false} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                  <p className="mono text-[9px] text-muted-foreground mt-1">Builds from the moment you start using the panel — refreshes every 15s.</p>
                </div>
              )}
              {dash.positions?.length > 0 && (
                <div className="overflow-x-auto">
                  <div className="label-xs text-muted-foreground px-4 pt-3 pb-1">Positions × AI Signal</div>
                  <table className="w-full text-sm">
                    <thead><tr className="label-xs text-muted-foreground border-b border-border/60">
                      {["Pair", "Side", "P/L", "AI Score", "Signal", "5d Bull"].map((h) => <th key={h} className="text-left font-normal px-4 py-2">{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {dash.positions.map((p) => (
                        <tr key={p.pair} className="border-b border-border/40" data-testid={`oanda-ai-${p.pair}`}>
                          <td className="px-4 py-2.5 mono font-medium">{pairLabel(p.pair)}</td>
                          <td className={`px-4 py-2.5 mono uppercase text-xs ${p.side === "long" ? "text-signal-green" : "text-signal-red"}`}>{p.side}</td>
                          <td className={`px-4 py-2.5 mono ${p.unrealized_pl >= 0 ? "text-signal-green" : "text-signal-red"}`}>{p.unrealized_pl >= 0 ? "+" : "-"}{fmt(Math.abs(p.unrealized_pl))}</td>
                          <td className="px-4 py-2.5 mono font-semibold">{p.ai_score ?? "—"}</td>
                          <td className={`px-4 py-2.5 mono uppercase text-xs ${p.direction === "bullish" ? "text-signal-green" : p.direction === "bearish" ? "text-signal-red" : "text-muted-foreground"}`}>{p.direction || "—"}</td>
                          <td className="px-4 py-2.5 mono">{p.forecast_5d?.prob_bull != null ? `${Math.round(p.forecast_5d.prob_bull * 100)}%` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="min-w-[110px]">
      <div className="label-xs text-muted-foreground">{label}</div>
      <div className={`mono text-lg font-semibold ${accent || ""}`}>{value}</div>
    </div>
  );
}
